import React, { useEffect, useRef, useState } from 'react';
import { Form } from 'react-final-form';
import { Button, FormHelperText, makeStyles } from '@material-ui/core';
import moment, { Moment } from 'moment';
import { useDataProvider, useNotify, useRefresh } from 'react-admin';
import { FORM_ERROR } from 'final-form';

import { Dialog } from 'components/common';
import { Checkbox, Input, Select, Switch } from 'components/form';
import { required } from 'components/form/validationRules';

import TypeChooser from './TypeChooser';
import { CreditCodeType, DeliveryService } from '../types';
import DateInputField from './DateInputField';
import TimeInputField from './TimeInputField';
import { getCurrencySymbol, SUPPORTED_CURRENCIES } from 'services/helpers/currency';

const useStyles = makeStyles((theme) => ({
  dialog: {
    '&&': {
      maxWidth: 650,
    },
  },
  dialogContent: {
    '&&': {
      minWidth: 0,
    },
  },
  inputsContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '24px',
    '& > *': {
      flex: 1,
      minWidth: 0,
    },
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      alignItems: 'stretch',
    },
  },
  datePickerContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
  date: {
    padding: theme.spacing(3, 0, 1.5),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  actions: {
    '& .MuiButton-label': {
      textTransform: 'none',
    },
  },
  input: {
    '& .MuiInput-input': {
      padding: theme.spacing(1, 0),
    },
  },
  inputRoot: {
    width: '100%',
  },
  deferred: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(1, 0, 0),
    borderTop: '1px solid #E0E0E0',
  },
  hint: {
    color: theme.palette.text.secondary,
    fontSize: 12,
  },
  excludedServicesSelect: {
    '&&': {
      height: 'auto',
      whiteSpace: 'normal',
      textOverflow: 'clip',
      overflow: 'visible',
      paddingRight: theme.spacing(3),
    },
  },
  excludedServicesValues: {
    display: 'flex',
    flexWrap: 'wrap',
    columnGap: theme.spacing(0.5),
  },
  excludedService: {
    flexShrink: 0,
    minWidth: 0,
    maxWidth: '100%',
    overflowWrap: 'break-word',
  },
}));

interface Props {
  open: boolean;
  onCancel: () => void;
}

interface FormValues {
  type: CreditCodeType;
  name: string;
  code: string;
  numberOfCodes: string;
  priceOfCodes: string;
  redeemLimitOfCodes: string;
  startAtDate: { date: Moment | null; time: Moment | null };
  endAtDate: { date: Moment | null; time: Moment | null };
  currency: string;
  deferredEnabled: boolean;
  deferred: {
    spendThreshold: string;
    earnWindowDays: string;
    requireVerifiedCard: boolean;
    excludeInternational: boolean;
    excludedServices: string[];
  };
}

interface DeliveryServicesState {
  items: DeliveryService[];
  status: 'loading' | 'loaded' | 'error';
  error?: string;
}

const wholeNumber = (minimum: number) => (value?: string) => {
  const message =
    minimum === 0 ? 'Must be a whole number of zero or greater' : 'Must be a positive whole number';

  if (!value || !/^\d+$/.test(value)) {
    return message;
  }

  const number = Number(value);

  if (!Number.isSafeInteger(number)) {
    return 'Value is too large';
  }

  return number >= minimum ? undefined : message;
};

const positiveWholeNumber = wholeNumber(1);
const nonNegativeWholeNumber = wholeNumber(0);

const validateCustomCode = (value?: string) => {
  const code = value?.trim();

  return code && !/^[a-zA-Z0-9]{4,20}$/.test(code)
    ? 'Must be 4 to 20 letters or digits'
    : undefined;
};

const validateNumberOfCodes = (value: string, values: FormValues) =>
  positiveWholeNumber(value) ||
  (values.code?.trim() && Number(value) !== 1
    ? 'Custom codes require exactly one code'
    : undefined);

const defaultValues: FormValues = {
  type: CreditCodeType.Fund,
  name: '',
  code: '',
  numberOfCodes: '',
  priceOfCodes: '',
  redeemLimitOfCodes: '',
  startAtDate: {
    date: null,
    time: null,
  },
  endAtDate: {
    date: null,
    time: null,
  },
  currency: 'USD',
  deferredEnabled: false,
  deferred: {
    spendThreshold: '',
    earnWindowDays: '30',
    requireVerifiedCard: true,
    excludeInternational: true,
    excludedServices: [],
  },
};

const getDate = ({ date, time }: { date: Moment | null; time: Moment | null }) => {
  if (!date || !time || !date.isValid() || !time.isValid()) {
    throw new Error('Select a valid date and time');
  }

  const selectedDateTime = `${date.format('YYYY-MM-DD')}T${time.format('HH:mm')}`;
  const dateTime = moment(selectedDateTime, 'YYYY-MM-DDTHH:mm', true);

  if (!dateTime.isValid()) {
    throw new Error('Select a valid date and time');
  }

  if (dateTime.format('YYYY-MM-DDTHH:mm') !== selectedDateTime) {
    throw new Error('Selected time does not exist on this date. Please choose another time');
  }

  return dateTime.format('YYYY-MM-DDTHH:mm:ss.SSSZ');
};

const fundCurrencies = SUPPORTED_CURRENCIES.map((item) => ({ value: item, label: item }));

const SERVICE_SEPARATOR = '|';

const serviceValue = ({ provider, code }: DeliveryService) =>
  `${provider}${SERVICE_SEPARATOR}${code}`;

const toExcludedService = (value: string) => {
  const [provider, serviceCode] = value.split(SERVICE_SEPARATOR);

  return { provider, serviceCode };
};

const CreateCampaignDialog = ({ open, onCancel }: Props) => {
  const classes = useStyles();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();
  const [deliveryServicesState, setDeliveryServicesState] = useState<DeliveryServicesState>({
    items: [],
    status: 'loading',
  });
  const [deliveryServicesRetry, setDeliveryServicesRetry] = useState(0);
  const deliveryServicesRequestId = useRef(0);

  useEffect(() => {
    let isMounted = true;
    const requestId = ++deliveryServicesRequestId.current;
    const isCurrentRequest = () => isMounted && requestId === deliveryServicesRequestId.current;

    setDeliveryServicesState((state) => ({ ...state, status: 'loading', error: undefined }));

    const fetchDeliveryServices = async () => {
      try {
        const { data } = await dataProvider.get('deliveryServices');

        if (!isCurrentRequest()) {
          return;
        }

        if (
          !Array.isArray(data) ||
          !data.every(
            (service) =>
              service &&
              typeof service.provider === 'string' &&
              !!service.provider.trim() &&
              typeof service.code === 'string' &&
              !!service.code.trim()
          )
        ) {
          throw new Error('Invalid carrier services response');
        }

        setDeliveryServicesState({ items: data, status: 'loaded' });
      } catch (error) {
        if (!isCurrentRequest()) {
          return;
        }

        const message = error?.message || 'Failed to load carrier services';
        setDeliveryServicesState((state) => ({ ...state, status: 'error', error: message }));
        notify(message, 'warning');
      }
    };

    fetchDeliveryServices();

    return () => {
      isMounted = false;
    };
  }, [dataProvider, notify, deliveryServicesRetry]);

  const handleRetryDeliveryServices = () => {
    if (deliveryServicesState.status !== 'error') {
      return;
    }

    setDeliveryServicesState((state) => ({ ...state, status: 'loading', error: undefined }));
    setDeliveryServicesRetry((retry) => retry + 1);
  };

  const servicesByValue = new Map<string, DeliveryService>();

  deliveryServicesState.items.forEach((service) => {
    const value = serviceValue(service);
    const existing = servicesByValue.get(value);
    const label = typeof service.label === 'string' ? service.label.trim() : '';

    if (!existing || (!existing.label && label)) {
      servicesByValue.set(value, { ...service, label });
    }
  });

  const serviceOptions = Array.from(servicesByValue.values()).map((service) => ({
    value: serviceValue(service),
    label: `${service.provider} — ${service.label || service.code}`,
  }));

  const handleSubmitForm = async (values: FormValues) => {
    try {
      if (
        values.type === CreditCodeType.Fund &&
        values.deferredEnabled &&
        deliveryServicesState.status !== 'loaded'
      ) {
        throw new Error(
          deliveryServicesState.error ||
            'Wait for carrier services to load before creating a deferred campaign'
        );
      }

      const startAtDate = getDate(values.startAtDate);
      const endAtDate = getDate(values.endAtDate);
      const today = moment();

      if (moment(startAtDate).isBefore(today)) {
        throw new Error('Start date is in the past. Please check your entries');
      }

      if (moment(endAtDate).isBefore(today)) {
        throw new Error('End date is in the past. Please check your entries');
      }

      if (moment(startAtDate).isAfter(moment(endAtDate))) {
        throw new Error('Start date is later than end date. Please check your entries');
      }

      const { deferredEnabled, deferred, code, ...campaign } = values;

      const requestData = {
        ...campaign,
        code: code?.trim() ? code.trim() : undefined,
        currency: values.type === CreditCodeType.Fund ? values.currency : 'LABEL',
        startAtDate: startAtDate,
        endAtDate: endAtDate,
        deferred:
          deferredEnabled && values.type === CreditCodeType.Fund
            ? {
                spendThreshold: Number(deferred.spendThreshold),
                earnWindowDays: Number(deferred.earnWindowDays),
                requireVerifiedCard: deferred.requireVerifiedCard,
                excludeInternational: deferred.excludeInternational,
                excludedServices: (deferred.excludedServices || []).map(toExcludedService),
              }
            : undefined,
      };

      const response = await dataProvider.post('creditCodes', { data: requestData });

      onCancel();

      refresh();

      return response;
    } catch (error) {
      return { [FORM_ERROR]: error?.message };
    }
  };

  return (
    <Form<FormValues>
      initialValues={defaultValues}
      onSubmit={handleSubmitForm}
      render={({ handleSubmit, submitting, values, submitError }) => {
        const isFund = values.type === CreditCodeType.Fund;

        return (
          <Dialog
            open={open}
            maxWidth="md"
            fullWidth
            onCancel={onCancel}
            title="Create campaign"
            onConfirm={handleSubmit}
            submitting={submitting}
            confirmDisabled={
              isFund && values.deferredEnabled && deliveryServicesState.status !== 'loaded'
            }
            classes={{
              root: classes.dialog,
              content: classes.dialogContent,
              actions: classes.actions,
            }}
            confirmText="Create"
            error={submitError}>
            <TypeChooser name="type" />

            <Input name="name" label="Campaign name" validate={required} />

            <div className={classes.inputsContainer}>
              {isFund && (
                <Select
                  name="currency"
                  label="Currency"
                  options={fundCurrencies}
                  validate={required}
                />
              )}

              <Input
                name="priceOfCodes"
                label="Amount"
                validate={positiveWholeNumber}
                classes={{ input: classes.input, root: classes.inputRoot }}
                inputProps={{
                  InputProps: {
                    startAdornment: isFund ? <span>{getCurrencySymbol(values.currency)}</span> : '',
                  },
                }}
                fullWidth
              />
            </div>

            <div className={classes.inputsContainer}>
              <Input
                name="numberOfCodes"
                label="Number of codes"
                validate={validateNumberOfCodes}
                classes={{ root: classes.inputRoot }}
                fullWidth
              />

              <Input
                name="redeemLimitOfCodes"
                classes={{ root: classes.inputRoot }}
                label="Usage limit per code (0 = unlimited)"
                validate={nonNegativeWholeNumber}
                fullWidth
              />
            </div>

            <Input
              name="code"
              label="Custom code (optional, one code only)"
              validate={validateCustomCode}
              inputProps={{ helperText: 'Letters and digits, 4 to 20 characters' }}
            />

            <div className={classes.date}>
              Start date
              <div className={classes.datePickerContainer}>
                <DateInputField
                  name="startAtDate.date"
                  validate={required}
                  label="Select date"
                  pickerProps={{
                    disablePast: true,
                    maxDate: values.endAtDate.date?.format('YYYY-MM-DD'),
                  }}
                />
                <TimeInputField name="startAtDate.time" validate={required} label="Select time" />
              </div>
            </div>

            <div className={classes.date}>
              End date
              <div className={classes.datePickerContainer}>
                <DateInputField
                  name="endAtDate.date"
                  validate={required}
                  label="Select date"
                  pickerProps={{
                    disablePast: true,
                  }}
                />
                <TimeInputField name="endAtDate.time" validate={required} label="Select time" />
              </div>
            </div>

            {isFund && (
              <div className={classes.deferred}>
                <Checkbox
                  name="deferredEnabled"
                  label="Deferred credit (granted after the account spends on labels)"
                />

                {values.deferredEnabled && (
                  <>
                    {deliveryServicesState.status === 'loading' && (
                      <div className={classes.hint} role="status">
                        Loading carrier services...
                      </div>
                    )}

                    {deliveryServicesState.status === 'error' && (
                      <div>
                        <FormHelperText error role="alert">
                          {deliveryServicesState.error}
                        </FormHelperText>
                        <Button onClick={handleRetryDeliveryServices} disabled={submitting}>
                          Retry
                        </Button>
                      </div>
                    )}

                    {deliveryServicesState.status === 'loaded' && !serviceOptions.length && (
                      <div className={classes.hint} role="status">
                        No carrier services available for exclusion.
                      </div>
                    )}

                    <div className={classes.inputsContainer}>
                      <Input
                        name="deferred.spendThreshold"
                        label="Spend on labels before the credit is granted"
                        validate={positiveWholeNumber}
                        classes={{ input: classes.input, root: classes.inputRoot }}
                        inputProps={{
                          InputProps: {
                            startAdornment: <span>{getCurrencySymbol(values.currency)}</span>,
                          },
                        }}
                        fullWidth
                      />

                      <Input
                        name="deferred.earnWindowDays"
                        label="Days after signup to earn it"
                        validate={positiveWholeNumber}
                        classes={{ root: classes.inputRoot }}
                        fullWidth
                      />
                    </div>

                    <Switch name="deferred.requireVerifiedCard" label="Require a verified card" />

                    <Switch
                      name="deferred.excludeInternational"
                      label="International labels do not count"
                    />

                    <Select
                      name="deferred.excludedServices"
                      label="Services that do not count"
                      options={serviceOptions}
                      disabled={deliveryServicesState.status !== 'loaded'}
                      inputProps={{
                        multiple: true,
                        displayEmpty: true,
                        classes: { selectMenu: classes.excludedServicesSelect },
                        renderValue: (selected: any) =>
                          (selected as string[]).length ? (
                            <div className={classes.excludedServicesValues}>
                              {(selected as string[]).map((value, index) => (
                                <span key={value} className={classes.excludedService}>
                                  {serviceOptions.find((option) => option.value === value)?.label ||
                                    value.split(SERVICE_SEPARATOR).join(' ')}
                                  {index < (selected as string[]).length - 1 ? ', ' : ''}
                                </span>
                              ))}
                            </div>
                          ) : (
                            'No services excluded'
                          ),
                      }}
                    />

                    <div className={classes.hint}>
                      Only accounts created after the campaign start can redeem a deferred code.
                    </div>
                  </>
                )}
              </div>
            )}
          </Dialog>
        );
      }}
    />
  );
};

export default CreateCampaignDialog;
